import { isValidElement, ReactNode } from "react";

/**
 * Extracts props from a React child element with type safety.
 *
 * This utility safely extracts the props object from a React element,
 * handling non-element children (strings, numbers, null, etc.) gracefully.
 *
 * @template T - The expected type of the props object
 * @param child - A React child node (element, string, number, null, etc.)
 * @returns The props object cast to type T if the child is a valid React element, undefined otherwise
 *
 * @example
 * ```tsx
 * interface ButtonProps {
 *   variant: 'primary' | 'secondary';
 *   disabled?: boolean;
 * }
 *
 * const child = <Button variant="primary" disabled />;
 * const props = getChildProps<ButtonProps>(child);
 * console.log(props?.variant); // 'primary'
 * console.log(props?.disabled); // true
 * ```
 *
 * @example
 * ```tsx
 * // Returns undefined for non-element children
 * getChildProps("Hello"); // undefined
 * getChildProps(null); // undefined
 * getChildProps(42); // undefined
 * ```
 */
export const getChildProps = <T = unknown>(child: ReactNode): T | undefined => {
  if (isValidElement(child) && child.props) {
    return child.props as T;
  }
  return undefined;
};
